import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getLocation } from '../utils/api/requests/requests';
import { corpLocationData } from '../utils/config/locationConfig';
import { Restaurant } from '../API';
export interface LocationState {
  userLocations: Restaurant[];
  userLocationsError: boolean;
  refetchLoading: boolean;
  activeLocation: string;
  verifiedLocation: string;
  locationAssigned: boolean;
  locationDetailsLoading: boolean;
  isModalLocationUpdate: boolean;
  subscribedLocations: string[];
  setLocationDetailsLoading: (val: boolean) => void;
  setRefetchLoading: (val: boolean) => void;
  setIsModalLocationUpdate: (val: boolean) => void;
  setActiveLocation: (loc: string) => void;
  confirmActiveLocation: (loc: string) => void;
  setLocationAssigned: (val: boolean) => void;
  setUserLocations: (loc: string[], token?: Nullable<string>) => void;
  setSubscribedLocations: (locs?: string[]) => void;
}

/**
 * Location numbers are numeric strings; '00000' is the corporate location.
 * The empty string is the store's own "unset" value and is allowed through.
 */
const isLocationNumber = (value: unknown): value is string =>
  typeof value === 'string' && (value === '' || /^\d+$/.test(value));

export const useLocationStore = create<LocationState>()(
  persist(
    set => ({
      userLocations: [],
      userLocationsError: false,
      refetchLoading: false,
      activeLocation: '',
      verifiedLocation: '',
      locationAssigned: false,
      locationDetailsLoading: false,
      isModalLocationUpdate: false,
      subscribedLocations: [],
      setActiveLocation: activeLocation => {
        if (!isLocationNumber(activeLocation)) return;
        set({ activeLocation });
      },
      confirmActiveLocation: location =>
        set(state =>
          // Ignore a response for a location the user has already left, so a
          // slow request cannot promote a stale value.
          isLocationNumber(location) && location === state.activeLocation
            ? { verifiedLocation: location }
            : state
        ),
      setRefetchLoading: refetchLoading => {
        set({ userLocationsError: false });
        set({ refetchLoading });
      },
      setIsModalLocationUpdate: isModalLocationUpdate =>
        set({ isModalLocationUpdate }),
      setLocationDetailsLoading: locationDetailsLoading => {
        set({ userLocationsError: false });
        set({ locationDetailsLoading });
      },
      setLocationAssigned: locationAssigned => set({ locationAssigned }),
      setUserLocations: async (locs, token) => {
        const fetchLocations = async (
          locs: string[],
          token?: Nullable<string>
        ) => {
          const locations: Restaurant[] = [];
          for (const loc of locs) {
            if (loc === '00000') {
              locations.push(corpLocationData as Restaurant);
              return locations;
            }
            try {
              const result = await getLocation(loc, token);
              locations.push(result as Restaurant);
            } catch (error) {
              set({ userLocationsError: true });
            }
          }
          return locations.sort(function (a, b) {
            return Number(a.number) - Number(b.number);
          });
        };
        set({ userLocations: await fetchLocations(locs, token) });
        set({ locationDetailsLoading: false });
        set({ refetchLoading: false });
      },
      setSubscribedLocations: subscribedLocations =>
        set(state => {
          const nextSubscribedLocations = subscribedLocations ?? [];
          const prevSubscribedLocations = state.subscribedLocations ?? [];

          const isSameValue =
            nextSubscribedLocations.length === prevSubscribedLocations.length &&
            nextSubscribedLocations.every(
              (location, index) => location === prevSubscribedLocations[index]
            );

          if (isSameValue) {
            return state;
          }

          return { subscribedLocations: nextSubscribedLocations };
        }),
    }),
    {
      // Only a location the API confirmed exists is mirrored to localStorage.
      // Until then the previous verified value stays put, so a reload during
      // verification restores the last known-good location rather than none.
      partialize: state => ({ activeLocation: state.verifiedLocation }),
      // Rehydration writes straight into state, bypassing setActiveLocation, so
      // browsers that persisted a bad value before this guard existed would
      // otherwise stay broken across reloads.
      merge: (persisted, current) => {
        const { activeLocation } = (persisted ?? {}) as Partial<
          Pick<LocationState, 'activeLocation'>
        >;

        if (!isLocationNumber(activeLocation)) return current;

        // A persisted value was verified before it was written, so it seeds
        // both fields and needs no re-check.
        return { ...current, activeLocation, verifiedLocation: activeLocation };
      },
      name: 'location-storage',
    }
  )
);
